import React, { useState, useEffect, useMemo } from 'react';
import { Package, MapPin, Info, Hash, FileText, AlertCircle, Loader2, BookOpen, Plus } from 'lucide-react';
import { useVessel } from '../../../../contexts/VesselContext';
import { useSwipeBack, SwipeIndicator } from '../../../../hooks/useSwipeBack';
// FT-357: shell-level back handler — bottom bar owns Back. Header BackButton removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';
import { getHardcodedVesselRequirements } from '../../../../config/hardcodedVesselRequirements';

// FT-531: exported so EquipmentDetailView's Category/Location edit UI can
// reuse the EXACT same control type (free-text input + fixed suggestion
// list) and options as this Add Equipment form, instead of inventing a
// different pattern or a divergent options list.
export const EQUIPMENT_CATEGORIES = [
  { value: 'FFE', label: 'Fire Fighting Equipment' },
  { value: 'LSA', label: 'Life Saving Appliances' },
  { value: 'Navigation', label: 'Navigation Equipment' },
  { value: 'GMDSS', label: 'GMDSS Equipment' },
  { value: 'Machinery', label: 'Machinery' },
  { value: 'Electrical', label: 'Electrical' },
  { value: 'Medical', label: 'Medical' },
  { value: 'Environmental', label: 'Environmental' },
  { value: 'Operations', label: 'Operations' },
  { value: 'Other', label: 'Other' }
];

export const COMMON_LOCATIONS = [
  'Bridge',
  'Engine Room',
  'Galley',
  'Control Position',
  'Deck - Forward',
  'Deck - Aft',
  'Port Side',
  'Starboard Side',
  'Wheelhouse',
  'Machinery Space',
  'Accommodation',
  'Cargo Hold',
  'Emergency Station'
];

const AddEquipmentView = ({ onBack, onSave, initialData = null, isSaving = false }) => {
  // FT-357: register onBack with the shell-level MobileBottomBar.
  useSetBackHandler(onBack);

  const { getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const { containerRef, swipeProgress } = useSwipeBack(onBack);
  
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    category: 'FFE',
    location: '',
    customCategory: '', // For custom category values

    // Details
    manufacturer: '',
    model: '',
    serialNumber: '',

    // Additional Information
    specifications: '',
    notes: '',

    // UI State
    showCategoryDropdown: false,
    showLocationDropdown: false,

    // Metadata
    source: 'manual',
    dateAdded: new Date().toISOString()
  });

  const [errors, setErrors] = useState({});

  // FT-50: Template picker state
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  // FT-50: Get vessel config directly from currentVessel (fields are on the object, not .config)
  const vesselConfig = currentVessel || {};

  // FT-50: Get equipment templates from hardcoded requirements, grouped by domain
  const equipmentTemplates = useMemo(() => {
    if (!vesselConfig || Object.keys(vesselConfig).length === 0) return {};

    try {
      const requirements = getHardcodedVesselRequirements(vesselConfig);
      const templates = {};

      // Collect all equipment from various sources
      const allEquipment = [
        ...(requirements.equipment || []),
        ...(requirements.basicEquipment || []),
        ...(requirements.areaCategoryEquipment || [])
      ];

      // Group by domain/category
      allEquipment.forEach(item => {
        const domain = item.domain || item.category || 'Other';
        if (!templates[domain]) {
          templates[domain] = [];
        }
        templates[domain].push({
          id: item.id || `${domain.toLowerCase()}_${item.name?.toLowerCase().replace(/\s+/g, '_') || 'item'}`,
          name: item.name || item.label || 'Unknown Equipment',
          category: domain,
          reference: item.reference || item.regulationRef || '',
          description: item.description || '',
          mandatory: item.mandatory || false
        });
      });

      return templates;
    } catch (error) {
      console.warn('Failed to load equipment templates:', error);
      return {};
    }
  }, [vesselConfig]);

  // FT-50: Flatten templates for list display
  const templateList = useMemo(() => {
    const list = [];
    Object.entries(equipmentTemplates).forEach(([domain, items]) => {
      items.forEach(item => {
        list.push({ ...item, domain });
      });
    });
    return list;
  }, [equipmentTemplates]);

  // FT-50: Apply template to form
  const applyTemplate = (template) => {
    setFormData(prev => ({
      ...prev,
      name: template.name,
      category: template.category || 'Other',
      notes: template.reference ? `WBC3 Reference: ${template.reference}` : ''
    }));
    setShowTemplatePicker(false);
  };

  // Equipment categories (FT-531: also exported below so EquipmentDetailView's
  // Category/Location edit UI reuses this exact list instead of duplicating it)
  const categories = EQUIPMENT_CATEGORIES;

  // Common locations
  const commonLocations = COMMON_LOCATIONS;

  // Populate form when editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || initialData.label || '',
        category: initialData.category || 'FFE',
        location: initialData.location || '',
        manufacturer: initialData.manufacturer || '',
        model: initialData.model || '',
        serialNumber: initialData.serialNumber || initialData.serialNo || '',
        specifications: initialData.specifications || '',
        notes: initialData.notes || '',
        source: initialData.source || 'manual',
        dateAdded: initialData.dateAdded || new Date().toISOString()
      });
    }
  }, [initialData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Equipment name is required';
    }
    if (!formData.category || (formData.category === 'Other' && !formData.customCategory?.trim())) {
      newErrors.category = 'Category is required';
    }
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    return newErrors;  // Return errors object instead of boolean
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      // Set errors state for display
      setErrors(validationErrors);

      // Scroll to first error (now using fresh validationErrors, not stale state)
      const firstErrorField = Object.keys(validationErrors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      return;
    }
    
    // Handle custom category
    const finalCategory = formData.category === 'Other' && formData.customCategory 
      ? formData.customCategory 
      : formData.category;
    
    // Generate ID if new equipment
    const equipmentData = {
      ...formData,
      category: finalCategory,
      id: initialData?.id || `${finalCategory.toLowerCase()}_${formData.name.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`,
      // Remove UI state fields
      showCategoryDropdown: undefined,
      showLocationDropdown: undefined,
      customCategory: undefined
    };
    
    // Clean up undefined fields
    Object.keys(equipmentData).forEach(key => {
      if (equipmentData[key] === undefined) {
        delete equipmentData[key];
      }
    });
    
    onSave(equipmentData);
  };

  return (
    <div className="min-h-screen bg-gray-50" ref={containerRef}>
      <SwipeIndicator progress={swipeProgress} />
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* FT-357: header BackButton removed — bottom bar owns Back. */}
              <div>
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                  {initialData ? 'Edit Equipment' : 'Add Equipment'}
                </h1>
                <p className="text-sm text-gray-500">
                  {currentVessel?.name || 'Equipment Register'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      {/* FT-83: pb-24 md:pb-0 prevents Save/Cancel buttons from being hidden behind mobile bottom nav */}
      <form onSubmit={handleSubmit}>
        <div className="max-w-3xl mx-auto p-6 pb-24 md:pb-6 space-y-6">

          {/* FT-50: Template Picker Section - Always show when adding new equipment */}
          {!initialData && (
            <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
              <div className="bg-cyan-600 px-5 py-3">
                <h2 className="text-base font-semibold text-white flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Start from Template
                </h2>
              </div>
              <div className="px-6 py-5">
                {templateList.length === 0 ? (
                  <div className="text-center py-4">
                    <BookOpen className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">
                      No templates available. Complete vessel configuration in Settings to see WBC3 equipment templates.
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      You can still add equipment manually using the form below.
                    </p>
                  </div>
                ) : !showTemplatePicker ? (
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowTemplatePicker(true)}
                      className="flex-1 px-4 py-3 text-sm font-medium text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-lg hover:bg-cyan-100 transition-colors"
                    >
                      <BookOpen className="inline h-4 w-4 mr-2" />
                      Choose from Templates ({templateList.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowTemplatePicker(false)}
                      className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <Plus className="inline h-4 w-4 mr-2" />
                      Create from Scratch
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600">Select equipment to pre-fill the form:</p>
                      <button
                        type="button"
                        onClick={() => setShowTemplatePicker(false)}
                        className="text-sm text-gray-500 hover:text-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                    <div className="max-h-64 overflow-y-auto space-y-2">
                      {Object.entries(equipmentTemplates).map(([domain, items]) => (
                        <div key={domain} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                            <span className="text-sm font-medium text-gray-700">{domain}</span>
                            <span className="text-xs text-gray-500 ml-2">({items.length})</span>
                          </div>
                          <div className="divide-y divide-gray-100">
                            {items.map((template) => (
                              <button
                                key={template.id}
                                type="button"
                                onClick={() => applyTemplate(template)}
                                className="w-full text-left px-3 py-2 hover:bg-cyan-50 transition-colors"
                              >
                                <div className="flex items-start justify-between">
                                  <div>
                                    <p className="font-medium text-gray-900 text-sm">{template.name}</p>
                                    {template.reference && (
                                      <p className="text-xs text-gray-500 mt-0.5">{template.reference}</p>
                                    )}
                                  </div>
                                  {template.mandatory && (
                                    <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">Required</span>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Basic Information Section */}
          <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white">Basic Information</h2>
            </div>
            <div className="px-6 py-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Equipment Name */}
              <div className="sm:col-span-2">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Equipment Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., Portable Fire Extinguisher - CO2"
                  className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ${
                    errors.name ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Category */}
              <div className="relative">
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                  <Package className="inline h-4 w-4 mr-1" />
                  Category <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    id="category"
                    type="text"
                    value={formData.category === 'Other' ? formData.customCategory || '' : 
                           categories.find(c => c.value === formData.category)?.label || formData.category}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      const matchedCategory = categories.find(c => 
                        c.label.toLowerCase() === inputValue.toLowerCase()
                      );
                      if (matchedCategory) {
                        handleInputChange('category', matchedCategory.value);
                        handleInputChange('customCategory', '');
                      } else {
                        handleInputChange('category', 'Other');
                        handleInputChange('customCategory', inputValue);
                      }
                    }}
                    onFocus={() => handleInputChange('showCategoryDropdown', true)}
                    onBlur={() => setTimeout(() => handleInputChange('showCategoryDropdown', false), 200)}
                    placeholder="Select or type category"
                    className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 bg-white ring-1 ring-inset ${
                      errors.category ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                    } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                  />
                  
                  {/* Dropdown with fixed positioning */}
                  {formData.showCategoryDropdown && (
                    <div className="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 max-h-60 overflow-auto">
                      <div className="py-1">
                        {categories.map(cat => (
                          <button
                            key={cat.value}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleInputChange('category', cat.value);
                              handleInputChange('customCategory', '');
                              handleInputChange('showCategoryDropdown', false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                          >
                            {cat.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {errors.category && (
                  <p className="mt-1 text-sm text-red-600">{errors.category}</p>
                )}
              </div>

              {/* Location */}
              <div className="relative">
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                  <MapPin className="inline h-4 w-4 mr-1" />
                  Location <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    id="location"
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    onFocus={() => handleInputChange('showLocationDropdown', true)}
                    onBlur={() => setTimeout(() => handleInputChange('showLocationDropdown', false), 200)}
                    placeholder="e.g., Bridge, Engine Room"
                    className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 bg-white ring-1 ring-inset ${
                      errors.location ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                    } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                  />
                  
                  {/* Dropdown with fixed positioning */}
                  {formData.showLocationDropdown && commonLocations.some(loc => 
                    loc.toLowerCase().includes(formData.location.toLowerCase())
                  ) && (
                    <div className="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 max-h-60 overflow-auto">
                      <div className="py-1">
                        {commonLocations
                          .filter(loc => loc.toLowerCase().includes(formData.location.toLowerCase()))
                          .map(loc => (
                            <button
                              key={loc}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleInputChange('location', loc);
                                handleInputChange('showLocationDropdown', false);
                              }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                            >
                              {loc}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
                {errors.location && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.location}
                  </p>
                )}
              </div>
            </div>
            </div>
          </div>

          {/* Equipment Details Section */}
          <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white">Equipment Details</h2>
            </div>
            <div className="px-6 py-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Manufacturer */}
              <div>
                <label htmlFor="manufacturer" className="block text-sm font-medium text-gray-700 mb-1">
                  Manufacturer
                </label>
                <input
                  id="manufacturer"
                  type="text"
                  value={formData.manufacturer}
                  onChange={(e) => handleInputChange('manufacturer', e.target.value)}
                  placeholder="e.g., Viking, Survitec"
                  className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                />
              </div>

              {/* Model */}
              <div>
                <label htmlFor="model" className="block text-sm font-medium text-gray-700 mb-1">
                  Model
                </label>
                <input
                  id="model"
                  type="text"
                  value={formData.model}
                  onChange={(e) => handleInputChange('model', e.target.value)}
                  placeholder="e.g., Model ABC-123"
                  className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                />
              </div>

              {/* Serial Number */}
              <div className="sm:col-span-2">
                <label htmlFor="serialNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  <Hash className="inline h-4 w-4 mr-1" />
                  Serial Number
                </label>
                <input
                  id="serialNumber"
                  type="text"
                  value={formData.serialNumber}
                  onChange={(e) => handleInputChange('serialNumber', e.target.value)}
                  placeholder="e.g., SN-2024-001234"
                  className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                />
              </div>
            </div>
            </div>
          </div>

          {/* Additional Information Section */}
          <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white">Additional Information</h2>
            </div>
            <div className="px-6 py-5">
              <div className="space-y-4">
              {/* Specifications */}
              <div>
                <label htmlFor="specifications" className="block text-sm font-medium text-gray-700 mb-1">
                  <Info className="inline h-4 w-4 mr-1" />
                  Specifications
                </label>
                <textarea
                  id="specifications"
                  value={formData.specifications}
                  onChange={(e) => handleInputChange('specifications', e.target.value)}
                  rows={3}
                  placeholder="e.g., Capacity: 5kg, Type: CO2, Pressure: 50 bar"
                  className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                />
              </div>

              {/* Notes */}
              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                  <FileText className="inline h-4 w-4 mr-1" />
                  Notes
                </label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  rows={3}
                  placeholder="Any additional notes or comments..."
                  className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                />
              </div>
            </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 sticky bottom-0 bg-gray-50 py-4">
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 border border-transparent rounded-md hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isSaving ? 'Saving...' : initialData ? 'Update Equipment' : 'Add Equipment'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddEquipmentView;